import type { FC, KeyboardEvent, ReactNode } from 'react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ManualOffers.module.scss';
import { IconExternal, IconPlus, TrashBinIcon } from '../../../assets/icons/icons';
import type { ManualSourceDraft } from '../../../hooks/useManualOffers';
import type { SupplierOffer } from '../../../types/results';
import { cx } from '../../../utils/cx';
import { formatRub } from '../../../utils/format';
import { Button } from '../../ui/Button/Button';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { OFFER_COL } from '../PositionDetails/offersColumns';
import { caretAfter, countBeforeCaret, groupPrice, stripSpaces } from '../../../utils/priceInput';
import { sanitizeRequestPriceInput } from '../../../utils/requestPrice';
import { HostMono } from '../../ui/HostMono/HostMono';
import {
    EMPTY_FORM,
    clearDraft,
    readDraft,
    writeDraft,
    type ManualOfferForm,
} from './manualOfferDraft';

const COLS = 7;

const URL_RE = /^https?:\/\/[^\s/.]+\.[^\s]+$/i;

const toNumber = (raw: string): number | null => {
    const cleaned = raw.replace(/[\s\u00a0]/g, '').replace(',', '.');
    if (!cleaned) return null;

    const value = Number(cleaned);
    return Number.isFinite(value) ? value : null;
};

const hostOf = (url: string): string => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch {
        return '';
    }
};

type FieldKey = 'url' | 'price' | 'server';

interface FormError {
    field: FieldKey;
    text: string;
}

interface ManualOffersProps {
    offers: SupplierOffer[];
    qty: number;
    showZone: boolean;
    deliveryOf: (offer: SupplierOffer) => number;
    withHeader: boolean;
    onAdd: (payload: ManualSourceDraft) => Promise<string | null>;
    draftKey: string;
    onDelete?: (offer: SupplierOffer) => void;
}

export const ManualOffers: FC<ManualOffersProps> = ({
    offers,
    qty,
    showZone,
    deliveryOf,
    withHeader,
    onAdd,
    draftKey,
    onDelete,
}) => {
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState<ManualOfferForm>(EMPTY_FORM);
    const [draft, setDraft] = useState<ManualOfferForm | null>(() => readDraft(draftKey));
    const [error, setError] = useState<FormError | null>(null);
    const [saving, setSaving] = useState(false);

    const nameRef = useRef<HTMLInputElement>(null);
    const priceRef = useRef<HTMLInputElement>(null);
    const priceCaret = useRef<number | null>(null);

    useEffect(() => {
        if (editing) writeDraft(draftKey, form);
    }, [editing, form, draftKey]);

    useEffect(() => {
        if (editing) nameRef.current?.focus();
    }, [editing]);

    useLayoutEffect(() => {
        if (priceCaret.current != null && priceRef.current) {
            priceRef.current.setSelectionRange(priceCaret.current, priceCaret.current);
            priceCaret.current = null;
        }
    });

    const open = () => {
        setEditing(true);
        setForm(readDraft(draftKey) ?? EMPTY_FORM);
        setError(null);
    };

    const close = () => {
        setEditing(false);
        setError(null);
        setDraft(readDraft(draftKey));
    };

    const discard = () => {
        clearDraft(draftKey);
        setDraft(null);
        setForm(EMPTY_FORM);
        setEditing(false);
        setError(null);
    };

    const patch = (next: Partial<ManualOfferForm>) => {
        setForm((prev) => ({ ...prev, ...next }));
        if (error) setError(null);
    };

    const price = toNumber(form.price);

    const submit = async () => {
        if (!editing || saving) return;

        const url = form.url.trim();

        if (!URL_RE.test(url)) {
            setError({ field: 'url', text: 'Укажите ссылку на товар — она начинается с http:// или https://' });
            return;
        }
        if (price == null || price <= 0) {
            setError({ field: 'price', text: 'Укажите цену за единицу больше нуля' });
            return;
        }

        const title = form.name.trim();
        const payload: ManualSourceDraft = {
            url,
            price: Math.round(price * 100) / 100,
            ...(title ? { title } : {}),
        };

        setSaving(true);
        try {
            const failure = await onAdd(payload);
            if (failure) setError({ field: 'server', text: failure });
            else discard();
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            void submit();
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
        }
    };

    const zoneCell = (content: ReactNode = <span className={styles.dash}>—</span>) => (
        <td className={cx(styles.num, !showZone && styles.zoneOff)}>{showZone ? content : null}</td>
    );

    const draftNote = (value: ManualOfferForm): string =>
        [hostOf(value.url), value.price && `${value.price} ₽`].filter(Boolean).join(' · ') ||
        'незаконченная ссылка';

    const renderForm = () => (
        <tr key="new" className={styles.editRow} onKeyDown={handleKeyDown}>
            <td colSpan={COLS}>
                <div className={styles.formGrid}>
                    <label className={styles.formField}>
                        <span className={styles.formLabel}>Наименование</span>
                        <input
                            ref={nameRef}
                            className={styles.field}
                            placeholder="Наименование товара"
                            value={form.name}
                            disabled={saving}
                            onChange={(event) => patch({ name: event.target.value })}
                        />
                    </label>

                    <label className={styles.formField}>
                        <span className={styles.formLabel}>Цена за ед., ₽</span>
                        <input
                            ref={priceRef}
                            className={cx(styles.field, styles.fieldNum, error?.field === 'price' && styles.fieldError)}
                            placeholder="0,00"
                            inputMode="decimal"
                            value={form.price}
                            disabled={saving}
                            onChange={(event) => {
                                const before = countBeforeCaret(event.target);
                                const text = groupPrice(sanitizeRequestPriceInput(stripSpaces(event.target.value)));

                                priceCaret.current = caretAfter(text, before);
                                patch({ price: text });
                            }}
                        />
                    </label>

                    <label className={styles.formField}>
                        <span className={styles.formLabel}>Ссылка</span>
                        <input
                            className={cx(styles.field, error?.field === 'url' && styles.fieldError)}
                            placeholder="https://…"
                            value={form.url}
                            disabled={saving}
                            onChange={(event) => patch({ url: event.target.value })}
                        />
                    </label>

                    <div className={styles.formFoot}>
                        <span className={cx(styles.hint, error && styles.hintError)}>
                            {error?.text ?? 'Ссылка и цена обязательны. Без наименования подставится адрес сайта.'}
                        </span>

                        <span className={styles.actions}>
                            <Button size="sm" disabled={saving} onClick={discard}>
                                Отмена
                            </Button>
                            <Button size="sm" variant="primary" disabled={saving} onClick={() => void submit()}>
                                {saving ? 'Добавляем…' : 'Добавить'}
                            </Button>
                        </span>
                    </div>
                </div>
            </td>
        </tr>
    );

    const renderOffer = (offer: SupplierOffer) => {
        const delivery = deliveryOf(offer);
        const total = delivery > 0 ? offer.price * qty + delivery : 0;

        return (
            <tr key={offer.id} className={styles.manualRow}>
                <td>
                    <span className={styles.seller}>
                        {offer.pic ? (
                            <CatalogThumb src={offer.pic} />
                        ) : (
                            <HostMono host={offer.host || hostOf(offer.url)} />
                        )}
                        <span className={styles.sellerText}>
                            <Tooltip text={offer.name}>
                                <b>{offer.name}</b>
                            </Tooltip>
                            <small>
                                {offer.host || hostOf(offer.url)}
                                <span className={styles.tag}>добавлено вручную</span>
                            </small>
                        </span>
                    </span>
                </td>
                <td className={styles.num}>{formatRub(offer.price)}</td>
                <td className={styles.num}>{qty} шт</td>
                <td className={styles.sum}>{formatRub(offer.price * qty)}</td>
                {zoneCell(delivery > 0 ? formatRub(delivery) : undefined)}
                {zoneCell(total > 0 ? formatRub(total) : undefined)}
                <td className={styles.actsCell}>
                    <span className={styles.acts}>
                        <a
                            className={styles.iconBtn}
                            href={offer.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            aria-label="Открыть ссылку"
                        >
                            <IconExternal />
                        </a>
                        {onDelete && offer.url && (
                            <button
                                type="button"
                                className={cx(styles.iconBtn, styles.danger)}
                                aria-label={`Удалить «${offer.name}»`}
                                onClick={() => onDelete(offer)}
                            >
                                <TrashBinIcon />
                            </button>
                        )}
                    </span>
                </td>
            </tr>
        );
    };

    return (
        <table className={cx(styles.table, withHeader && styles.standalone)}>
            <colgroup>
                <col />
                <col style={{ width: `${OFFER_COL.price}px` }} />
                <col style={{ width: `${OFFER_COL.qty}px` }} />
                <col style={{ width: `${OFFER_COL.cost}px` }} />
                <col style={{ width: showZone ? `${OFFER_COL.logistics}px` : '0' }} />
                <col style={{ width: showZone ? `${OFFER_COL.total}px` : '0' }} />
                <col style={{ width: `${OFFER_COL.actions}px` }} />
            </colgroup>

            {withHeader && (offers.length > 0 || editing) && (
                <thead>
                    <tr>
                        <th>Продавец</th>
                        <th>Цена за ед.</th>
                        <th>Кол-во</th>
                        <th>Стоимость</th>
                        <th className={cx(!showZone && styles.zoneOff)}>{showZone && 'Логистика'}</th>
                        <th className={cx(!showZone && styles.zoneOff)}>{showZone && 'Итого с доставкой'}</th>
                        <th />
                    </tr>
                </thead>
            )}

            <tbody>
                {offers.map(renderOffer)}

                {editing ? (
                    renderForm()
                ) : (
                    <tr className={styles.add}>
                        <td colSpan={COLS}>
                            <button type="button" className={styles.addBtn} onClick={open}>
                                <span className={styles.addIcon}>
                                    <IconPlus />
                                </span>
                                {draft
                                    ? 'Вернуться к черновику'
                                    : offers.length
                                        ? 'Добавить ещё ссылку'
                                        : 'Добавить предложение по ссылке'}
                                <span className={styles.addNote}>
                                    {draft
                                        ? draftNote(draft)
                                        : 'ссылка на товар у поставщика · пропадёт при повторном поиске'}
                                </span>
                            </button>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};